/* Copyright 2016 The MathWorks, Inc. */

define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/on",
    "dijit/_WidgetBase",
    "dijit/_TemplatedMixin",
    "dijit/_CssStateMixin",
    "dijit/layout/TabContainer",
    "dijit/layout/ScrollingTabController",
    "mw-ddux/UsageLogger",
    "mw-form/mixins/property/TextMixin",
    "mw-form/mixins/event/ChangeEventMixin",
    "mw-form/mixins/property/CheckedMixin",
    "MW/toolstrip/mixins/property/RemovableMixin",
    "mw-form/mixins/ResizeMixin",
    "mw-form/mixins/PreventSelectionMixin",
    "mw-form/mixins/mixinDependencyValidator",
    "mw-form/mixins/AriaLabelledByMixin",
    "dojo/text!./templates/TabButton.html"
], function (declare, lang, on, _WidgetBase, _TemplatedMixin, _CssStateMixin, TabContainer, ScrollingTabController, UsageLogger,
             TextMixin, ChangeEventMixin, CheckedMixin, RemovableMixin, ResizeMixin,
             PreventSelectionMixin, mixinDependencyValidator, AriaLabelledByMixin, template) {

    var attributeFilters = ["style", "class"];
    function parseMutationsList(mutationsList) {
        mutationsList.forEach(function (mutation) {
            if (attributeFilters.indexOf(mutation.attributeName) > -1) {
                this.resize();
            }
        }.bind(this));
    };

    var TabButton = declare(mixinDependencyValidator.validate([
        _WidgetBase, _TemplatedMixin, _CssStateMixin, TextMixin, ChangeEventMixin, CheckedMixin,
        RemovableMixin, ResizeMixin, PreventSelectionMixin, AriaLabelledByMixin
    ]), {
        // Keeping "dijitTab" (and "dijitTabInner" & "dijitTabContent" in template) for backwards
        // compatibility with css, tests, testers, etc... 
        baseClass: "mwWidget mwTabButton dijitTab",
        templateString: template,

        postCreate: function(){
            this.inherited(arguments);

            this.mutationObserver = new MutationObserver(parseMutationsList.bind(this));
            this.mutationObserver.observe(this.domNode, {
                attributes: true,
                attributeFilter: attributeFilters
            });

            var closeTab = function (e) {
                // use setTimeout to avoid issue in StackController.js where it tries to manage close
                setTimeout(function () {
                    this.page._emitRemoveEvent();
                }.bind(this), 0);
            }.bind(this);
            this.closeNode.addEventListener("click", closeTab);

            var targetNode = null;
            var middleMouseDownListener = function (e) {
                if (e.button === 1) {
                    targetNode = this.domNode;
                    window.addEventListener("mouseup", middleMouseUpListener, true);
                }
            }.bind(this);
            var middleMouseUpListener = function (e) {
                window.removeEventListener("mouseup", middleMouseUpListener, true);
                if ((targetNode.contains(e.target)) && (e.button === 1)) {
                    closeTab();
                }
                targetNode = null;
            }.bind(this);
            this.domNode.addEventListener("mousedown", middleMouseDownListener);

            var keyupListener = function (e) {
                if (e.keyCode === 46) { // [Delete]
                    closeTab();
                }
            }.bind(this);
            this.focusNode.addEventListener("keyup", keyupListener);

            this.own({
                remove: function () {
                    this.closeNode.removeEventListener("click", closeTab);
                    this.domNode.removeEventListener("mousedown", middleMouseDownListener);
                    window.removeEventListener("mouseup", middleMouseUpListener, true);
                    this.focusNode.removeEventListener("keyup", keyupListener);
                }.bind(this)
            })
        },

        _setLabelAttr: function (value) {
            this.inherited(arguments);
            this.set("text", value);
        },

        _getLabelAttr: function (value) {
            this.inherited(arguments);
            return this.get("text");
        },
        
        _setCheckedAttr: function () {
            this.inherited(arguments);
            this._setAriaSelected();
        },

        _setAriaSelected: function () {
            var node = this.focusNode ? this.focusNode : this.domNode;
            // Only tabs should have an aria-selected attribute.
            if (node.hasAttribute('aria-selected')) {
                node.setAttribute('aria-selected', this.get('checked'));
            }
        },

        resize: function () {
            this.inherited(arguments);

            this.textNode.style.textOverflow = "clip";
            if (this.textNode.offsetWidth < this.textNode.scrollWidth) {
                this.domNode.setAttribute("title", this.get("text"));
            } else {
                this.domNode.removeAttribute("title");
            }
            this.textNode.style.textOverflow = "";
        },

        destroy: function () {
            this.mutationObserver.disconnect();
            delete this.mutationObserver;
            this.inherited(arguments);
        }
    });
    
    return declare(TabContainer, {
        postMixInProperties: function () {
            this.inherited(arguments);
            this.controllerWidget = declare(ScrollingTabController, {
                buttonWidget: TabButton,
                resize: function () {},
                onAddChild: function (page, insertIndex) {
                    var Cls = lang.isString(this.buttonWidget) ? lang.getObject(this.buttonWidget) : this.buttonWidget;
                    var button = new Cls({
                        id: this.id + "_" + page.id,
                        name: this.id + "_" + page.id, // note: must match id used in pane2button()
                        text: page.title,
                        disabled: page.disabled,
                        ownerDocument: this.ownerDocument,
                        dir: page.dir,
                        lang: page.lang,
                        page: page,
                        removable: page.removable || false,
                    });

                    this.addChild(button, insertIndex);
                    var buttonTag = page.tag + ".tab";
                    button.domNode.setAttribute("data-tag", buttonTag);
                    button.domNode.setAttribute("data-type", "ToolstripTab");
                    page.controlButton = button;	// this value might be overwritten if two tabs point to same container
                    if(!this._currentChild){
                        // If this is the first child then StackContainer will soon publish that it's selected,
                        // but before that StackContainer calls layout(), and before layout() is called the
                        // StackController needs to have the proper height... which means that the button needs
                        // to be marked as selected now.   See test_TabContainer_CSS.html for test.
                        this.onSelectChild(page);
                    }

                    // Add this StackController button to the list of things that labels that StackContainer pane.
                    // Also, if there's an aria-labelledby parameter for the pane, then the aria-label parameter is unneeded.
                    var labelledby = page._wrapper.getAttribute("aria-labelledby") ?
                    page._wrapper.getAttribute("aria-labelledby") + " " + button.id : button.id;
                    page._wrapper.removeAttribute("aria-label");
                    page._wrapper.setAttribute("aria-labelledby", labelledby);

                    this.getChildren().forEach(function (tabButton) {
                        tabButton.resize();
                    });
                }
            });
        },
        resize: function () {
            if(!this._hasBeenShown){
                this._hasBeenShown = true;
                var selected = this.selectedChildWidget;
                if(selected){
                    this._showChild(selected);
                }
            }
            var sc = this.selectedChildWidget;
            if (sc) {
                sc.resize();
            }
        }
    });
});
